import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';

const SearchBar = ({ placeholder, onSearch, onResults, onResultClick, className = "", clearSearch }) => {
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searchCompleted, setSearchCompleted] = useState(false);

  // Use refs to avoid recreating the debounced function when callbacks change
  const onSearchRef = useRef(onSearch);
  const onResultsRef = useRef(onResults);
  
  useEffect(() => {
    onSearchRef.current = onSearch;
    onResultsRef.current = onResults;
  }, [onSearch, onResults]);

  // Process search results to handle ExistingPatientCodes structure
  const processSearchResults = useCallback((searchResults) => {
    if (!searchResults) return [];
    
    // Check if results have the ExistingPatientCodes structure
    if (searchResults.activeCodes || searchResults.significantPastCodes) {
      const processedResults = [];
      
      // Add active codes with category label
      if (searchResults.activeCodes && searchResults.activeCodes.length > 0) {
        searchResults.activeCodes.forEach(existingCode => {
          // Keep the full ExistingCode object structure
          processedResults.push({
            ...existingCode,
            category: 'Active'
          });
        });
      }
      
      // Add significant past codes with category label
      if (searchResults.significantPastCodes && searchResults.significantPastCodes.length > 0) {
        searchResults.significantPastCodes.forEach(existingCode => {
          // Keep the full ExistingCode object structure
          processedResults.push({
            ...existingCode,
            category: 'Past'
          });
        });
      }
      
      return processedResults;
    }
    
    // If it's already an array, return as is
    return Array.isArray(searchResults) ? searchResults : [];
  }, []);

  // Memoized debounced search function - uses refs to avoid recreating
  const debouncedSearch = useMemo(() => 
    debounce(async (searchQuery) => {
      if (!searchQuery.trim()) {
        setResults([]);
        setShowResults(false);
        setIsSearching(false);
        setSearchCompleted(false);
        return;
      }

      setIsSearching(true);
      try {
        await onSearchRef.current(searchQuery, (searchResults) => {          
          const processedResults = processSearchResults(searchResults);
          setResults(processedResults);
          setShowResults(true);
          setSearchCompleted(true);
          setIsSearching(false);
          if (onResultsRef.current) {
            onResultsRef.current(processedResults);
          }
        });
      } catch (error) {
        console.error('Search error:', error);
        setIsSearching(false);
        setResults([]);
        setShowResults(true);
        setSearchCompleted(true);
      }
    }, 500), // 500ms delay
    [processSearchResults]
  );

  // Handle input changes
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setQuery(value);
    
    if (value.trim()) {
      setIsSearching(true);
      setSearchCompleted(false);
      debouncedSearch(value);
    } else {
      setResults([]);
      setShowResults(false);
      setIsSearching(false);
      setSearchCompleted(false);
    }
  }, [debouncedSearch]);

  // Handle result click
  const handleResultClick = useCallback((result) => {
    setQuery('');
    setResults([]);
    setShowResults(false);
    setSearchCompleted(false);
    
    // Call the provided onResultClick handler if available
    if (onResultClick) {
      onResultClick(result);
    }
  }, [onResultClick]);

  // Clear search function that can be called by parent
  const clearSearchInternal = useCallback(() => {
    setQuery('');
    setResults([]);
    setShowResults(false);
    setSearchCompleted(false);
    setIsSearching(false);
  }, []);

  // Expose clear function to parent via callback
  React.useEffect(() => {
    if (clearSearch) {
      clearSearch(() => clearSearchInternal);
    }
  }, [clearSearch, clearSearchInternal]);

  // Handle clicking outside to close results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.search-bar-container')) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`search-bar-container ${className}`}>
      <div className="search-bar-input-container">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          placeholder={placeholder}
          className="search-bar-input"
        />
        {isSearching ? (
          <div className="search-bar-spinner">
            <div className="spinner-circle-small"></div>
          </div>
        ) : (
          <div className="search-bar-icon">
            <FontAwesomeIcon icon={faSearch} />
          </div>
        )}
      </div>
      
      {showResults && searchCompleted && (
        <div className="search-bar-results">
          {results.length > 0 ? (
            results.map((result, index) => (
              <div
                key={index}
                className="search-bar-result-item"
                onClick={() => handleResultClick(result)}
              >
                <div className="search-result-code">
                  ({result.code?.code || result.code})
                  {result.category && (
                    <span className={`search-result-category ${result.category.toLowerCase()}`}>
                      {result.category}
                    </span>
                  )}
                </div>
                <div className="search-result-description">{result.code?.terms?.[0] || result.terms?.[0] || result.description}</div>
              </div>
            ))
          ) : (
            <div className="search-bar-no-results">
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default React.memo(SearchBar); 